import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Plus, Trash2, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const getN = (obj, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
const setN = (obj, path, val) => {
  const keys = path.split(".");
  const clone = structuredClone(obj);
  let cur = clone;
  keys.slice(0, -1).forEach((k) => (cur = cur[k]));
  cur[keys[keys.length - 1]] = val;
  return clone;
};

const CONTENT_FIELDS = [
  { path: "whatsapp", label: "Nomor WhatsApp (format 62...)" },
  { path: "contact.email", label: "Email Kontak" },
  { path: "contact.phone", label: "Telepon Kontak" },
  { path: "contact.address", label: "Alamat", bi: true },
  { path: "hero.lines.0", label: "Hero — Baris 1", bi: true },
  { path: "hero.lines.1", label: "Hero — Baris 2", bi: true },
  { path: "hero.lines.2", label: "Hero — Baris 3", bi: true },
  { path: "hero.sub", label: "Hero — Sub-judul", bi: true, area: true },
  { path: "contact.heading", label: "Kontak — Judul", bi: true },
  { path: "contact.body", label: "Kontak — Teks", bi: true, area: true },
  { path: "stats.0.value", label: "Statistik 1 — Nilai" },
  { path: "stats.1.value", label: "Statistik 2 — Nilai" },
  { path: "stats.2.value", label: "Statistik 3 — Nilai" },
  { path: "stats.3.value", label: "Statistik 4 — Nilai" },
  ...[0, 1, 2, 3].flatMap((i) => [
    { path: `manifesto.chapters.${i}.title`, label: `Manifesto ${i + 1} — Judul`, bi: true },
    { path: `manifesto.chapters.${i}.body`, label: `Manifesto ${i + 1} — Isi`, bi: true, area: true },
  ]),
];

const EMPTY_PRODUCT = {
  name: { id: "", en: "" },
  tag: { id: "", en: "" },
  desc: { id: "", en: "" },
  price: "",
  unit: { id: "", en: "" },
  image: "",
};

const inputCls =
  "w-full rounded-lg border border-[#1E3A2B]/20 bg-white px-3.5 py-2.5 text-sm text-[#1A231E] focus:outline-none focus:border-[#D96B27] transition-colors";

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#14281E] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-[#FAF7F2] p-8 shadow-2xl" data-testid="admin-login-form">
        <div className="flex items-center gap-2.5 mb-8">
          <img src="/logo-mark.png" alt="Yutaka Agro" className="w-12 h-auto" data-testid="admin-login-logo" />
          <div className="leading-none">
            <p className="font-sans font-extrabold tracking-[0.18em] text-[#1E3A2B] text-sm">YUTAKA AGRO</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#5C6B61] mt-1">Admin Panel</p>
          </div>
        </div>
        <label className="block text-xs font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Email</label>
        <input
          data-testid="admin-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls + " mb-4"}
          placeholder="admin@yutakaagro.id"
        />
        <label className="block text-xs font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Kata Sandi</label>
        <input
          data-testid="admin-password-input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputCls + " mb-4"}
          placeholder="••••••••"
        />
        {error && (
          <p className="mb-4 text-sm text-red-600" data-testid="admin-login-error">{error}</p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-full bg-[#1E3A2B] text-[#FAF7F2] text-sm font-semibold hover:bg-[#D96B27] transition-colors disabled:opacity-50"
        >
          {loading ? "Memproses..." : "Masuk"}
        </button>
        <Link to="/" className="mt-5 flex items-center justify-center gap-2 text-xs text-[#5C6B61] hover:text-[#D96B27] transition-colors" data-testid="admin-back-home">
          <ArrowLeft size={13} /> Kembali ke beranda
        </Link>
      </form>
    </div>
  );
}

function Field({ field, content, onChange }) {
  if (field.bi) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {["id", "en"].map((l) => (
          <div key={l}>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">
              {field.label} ({l.toUpperCase()})
            </label>
            {field.area ? (
              <textarea
                data-testid={`field-${field.path}-${l}`}
                rows={3}
                value={getN(content, field.path)?.[l] ?? ""}
                onChange={(e) => onChange(setN(content, `${field.path}.${l}`, e.target.value))}
                className={inputCls}
              />
            ) : (
              <input
                data-testid={`field-${field.path}-${l}`}
                value={getN(content, field.path)?.[l] ?? ""}
                onChange={(e) => onChange(setN(content, `${field.path}.${l}`, e.target.value))}
                className={inputCls}
              />
            )}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">{field.label}</label>
      <input
        data-testid={`field-${field.path}`}
        value={getN(content, field.path) ?? ""}
        onChange={(e) => onChange(setN(content, field.path, e.target.value))}
        className={inputCls}
      />
    </div>
  );
}

function ContentTab({ content, setContent }) {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", content);
      setContent(data);
      toast.success("Konten berhasil disimpan");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="admin-content-tab">
      <div className="flex flex-col gap-5">
        {CONTENT_FIELDS.map((f) => (
          <Field key={f.path} field={f} content={content} onChange={setContent} />
        ))}
      </div>
      <button
        data-testid="admin-save-content-button"
        onClick={save}
        disabled={saving}
        className="mt-8 flex items-center gap-2 px-7 py-3 rounded-full bg-[#1E3A2B] text-[#FAF7F2] text-sm font-semibold hover:bg-[#D96B27] transition-colors disabled:opacity-50"
      >
        <Save size={15} /> {saving ? "Menyimpan..." : "Simpan Konten"}
      </button>
    </div>
  );
}

function ProductsTab({ products, setProducts }) {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const pick = (p) => {
    setSelected(p.id);
    setForm(structuredClone(p));
  };
  const addNew = () => {
    setSelected(null);
    setForm(structuredClone(EMPTY_PRODUCT));
  };
  const set = (path, val) => setForm((f) => setN(f, path, val));

  const save = async () => {
    setSaving(true);
    try {
      if (selected) {
        const { data } = await api.put(`/products/${selected}`, form);
        setProducts(products.map((p) => (p.id === selected ? data : p)));
      } else {
        const { data } = await api.post("/products", form);
        setProducts([...products, data]);
        setSelected(data.id);
      }
      toast.success("Produk berhasil disimpan");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      setProducts(products.filter((p) => p.id !== id));
      if (selected === id) {
        setSelected(null);
        setForm(null);
      }
      toast.success("Produk dihapus");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="admin-products-tab">
      <div>
        <button
          data-testid="product-add-button"
          onClick={addNew}
          className="mb-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D96B27] text-[#FAF7F2] text-xs font-semibold hover:bg-[#1E3A2B] transition-colors"
        >
          <Plus size={14} /> Tambah Produk
        </button>
        <div className="flex flex-col gap-2">
          {products.map((p, i) => (
            <div
              key={p.id}
              className={`flex items-center justify-between rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                selected === p.id ? "border-[#D96B27] bg-[#D96B27]/5" : "border-[#1E3A2B]/15 bg-white hover:border-[#1E3A2B]/40"
              }`}
              onClick={() => pick(p)}
              data-testid={`product-item-${i}`}
            >
              <span className="font-serif font-semibold text-[#1E3A2B]">{p.name?.id}</span>
              <button
                data-testid={`product-delete-button-${i}`}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(p.id);
                }}
                className="p-1.5 text-[#5C6B61] hover:text-red-600 transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2">
        {form ? (
          <div className="flex flex-col gap-4" data-testid="product-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Nama (ID)</label>
                <input data-testid="product-name-id" value={form.name.id} onChange={(e) => set("name.id", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Nama (EN)</label>
                <input data-testid="product-name-en" value={form.name.en} onChange={(e) => set("name.en", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Kategori (ID)</label>
                <input data-testid="product-tag-id" value={form.tag.id} onChange={(e) => set("tag.id", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Kategori (EN)</label>
                <input data-testid="product-tag-en" value={form.tag.en} onChange={(e) => set("tag.en", e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Deskripsi (ID)</label>
                <textarea data-testid="product-desc-id" rows={3} value={form.desc.id} onChange={(e) => set("desc.id", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Deskripsi (EN)</label>
                <textarea data-testid="product-desc-en" rows={3} value={form.desc.en} onChange={(e) => set("desc.en", e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Harga</label>
                <input data-testid="product-price" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} placeholder="Rp 10.000" />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Satuan (ID)</label>
                <input data-testid="product-unit-id" value={form.unit.id} onChange={(e) => set("unit.id", e.target.value)} className={inputCls} placeholder="/kg" />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">Satuan (EN)</label>
                <input data-testid="product-unit-en" value={form.unit.en} onChange={(e) => set("unit.en", e.target.value)} className={inputCls} placeholder="/kg" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#5C6B61] mb-1.5">URL Gambar</label>
              <input data-testid="product-image" value={form.image} onChange={(e) => set("image", e.target.value)} className={inputCls} placeholder="https://..." />
            </div>
            {form.image && (
              <img src={form.image} alt="preview" className="w-40 h-28 object-cover rounded-lg border border-[#1E3A2B]/15" data-testid="product-image-preview" />
            )}
            <button
              data-testid="product-save-button"
              onClick={save}
              disabled={saving}
              className="self-start flex items-center gap-2 px-7 py-3 rounded-full bg-[#1E3A2B] text-[#FAF7F2] text-sm font-semibold hover:bg-[#D96B27] transition-colors disabled:opacity-50"
            >
              <Save size={15} /> {saving ? "Menyimpan..." : "Simpan Produk"}
            </button>
          </div>
        ) : (
          <p className="text-sm text-[#5C6B61]" data-testid="product-form-empty">
            Pilih produk untuk diedit, atau tambah produk baru.
          </p>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { content, setContent, products, setProducts } = useApp();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("konten");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === null) {
    return <div className="min-h-screen bg-[#14281E]" data-testid="admin-loading" />;
  }
  if (user === false) {
    return <LoginForm onSuccess={setUser} />;
  }

  return (
    <div className="min-h-screen bg-[#F2ECE1]" data-testid="admin-panel">
      <header className="bg-[#14281E] px-4 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-mark.png" alt="Yutaka Agro" className="w-10 h-auto" />
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FAF7F2]/80">Yutaka Agro — Admin</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-xs text-[#FAF7F2]/60 hover:text-[#E5B869] transition-colors flex items-center gap-1.5" data-testid="admin-view-site">
            <ArrowLeft size={13} /> Lihat Situs
          </Link>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-1.5 text-xs text-[#FAF7F2]/60 hover:text-[#D96B27] transition-colors"
          >
            <LogOut size={13} /> Keluar
          </button>
        </div>
      </header>

      <div className="px-4 sm:px-8 py-8 max-w-5xl mx-auto">
        <div className="flex gap-2 mb-8">
          {[
            { key: "konten", label: "Konten Situs" },
            { key: "produk", label: "Produk" },
          ].map((tb) => (
            <button
              key={tb.key}
              data-testid={`admin-tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`px-5 py-2.5 rounded-full text-xs font-semibold tracking-wide transition-colors ${
                tab === tb.key ? "bg-[#1E3A2B] text-[#FAF7F2]" : "bg-white text-[#1E3A2B] border border-[#1E3A2B]/15 hover:border-[#D96B27]"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {content ? (
          tab === "konten" ? (
            <ContentTab content={content} setContent={setContent} />
          ) : (
            <ProductsTab products={products} setProducts={setProducts} />
          )
        ) : (
          <p className="text-sm text-[#5C6B61]">Memuat konten...</p>
        )}
      </div>
    </div>
  );
}
